import { smallerClosingCross } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import "./input-no-spinner.css";

export type BasicCustomFilterProps = {
  label?: string;
  minValue: number;
  maxValue: number;
  onChangeMin: (value: number) => void;
  onChangeMax: (value: number) => void;
  isTitle?: boolean;
  minPlaceHolder?: number;
  maxPlaceHolder?: number;
  large?: boolean;
  slim?: boolean;
  isOnSbcFilterSidebar?: boolean;
  /** Paint the boxes on the 2.0 neutral ramp: bordered, neutral-900 fill. */
  newDesignSystem?: boolean;
};

// Basic custom filter component, for when the filter has a min and max value
export const RangeFilter = ({
  label,
  minValue,
  maxValue,
  onChangeMin,
  onChangeMax,
  isTitle,
  minPlaceHolder,
  maxPlaceHolder,
  large,
  slim = false,
  isOnSbcFilterSidebar,
  newDesignSystem = false,
}: BasicCustomFilterProps) => {
  const [isMinFocused, setMinFocused] = useState(false);
  const [isMaxFocused, setMaxFocused] = useState(false);

  return (
    <div
      className={cn(
        "flex justify-between group",
        isOnSbcFilterSidebar || newDesignSystem
          ? "hover-none"
          : "hover:bg-white/5",
        !slim && " p-1",
        isTitle ? "border-b-2 border-white/20 rounded-t-md" : "rounded-md",
      )}
    >
      <span
        className={cn(
          "my-auto font-medium",
          isTitle
            ? "text-sm font-bold text-secondary-dark"
            : "text-xs text-gray-300",
        )}
      >
        {label}
      </span>
      <div className="flex gap-x-1">
        {!isOnSbcFilterSidebar &&
          !newDesignSystem &&
          (minValue || maxValue) && (
            <button
              className="flex desktop:hidden desktop:group-hover:flex items-center justify-center w-4 h-4 my-auto rounded-full bg-primary-red fill-white"
              onClick={(e) => {
                onChangeMin(null);
                onChangeMax(null);
                e.stopPropagation();
              }}
            >
              {smallerClosingCross}
            </button>
          )}
        <InputField
          onChange={onChangeMin}
          onFocus={() => setMinFocused(true)}
          onBlur={(val) => {
            const max = maxPlaceHolder || 99;
            if (maxValue && val > maxValue) onChangeMax(val > max ? max : val);
            setMinFocused(false);
          }}
          value={minValue}
          placeHolder={isMinFocused ? "" : minPlaceHolder?.toString() || "1"}
          isFocused={isMinFocused}
          min={minPlaceHolder}
          max={maxPlaceHolder}
          large={large}
          isOnSbcFilterSidebar={isOnSbcFilterSidebar}
          newDesignSystem={newDesignSystem}
        />
        <InputField
          onChange={onChangeMax}
          onFocus={() => setMaxFocused(true)}
          onBlur={(val) => {
            const min = minPlaceHolder || 1;
            if (minValue && val < minValue) onChangeMin(val < min ? min : val);
            setMaxFocused(false);
          }}
          value={maxValue}
          placeHolder={isMaxFocused ? "" : maxPlaceHolder?.toString() || "99"}
          isFocused={isMaxFocused}
          max={maxPlaceHolder}
          min={minPlaceHolder}
          large={large}
          isOnSbcFilterSidebar={isOnSbcFilterSidebar}
          newDesignSystem={newDesignSystem}
        />
      </div>
    </div>
  );
};

type InputFieldProps = {
  onChange: (value: number) => void;
  onFocus: () => void;
  onBlur: (val: number) => void;
  value: number;
  placeHolder: string;
  isFocused: boolean;
  min?: number;
  max?: number;
  large?: boolean;
  isOnSbcFilterSidebar?: boolean;
  newDesignSystem?: boolean;
};

export const InputField = ({
  onChange,
  onFocus,
  onBlur,
  value,
  placeHolder,
  isFocused,
  min,
  max,
  large,
  isOnSbcFilterSidebar,
  newDesignSystem = false,
}: InputFieldProps) => {
  const { t } = useTranslation("common");
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  if (newDesignSystem) {
    // The 2.0 box: the sidebar variant's behaviour with the panel's paint.
    isOnSbcFilterSidebar = true;
  }
  return (
    <input
      className={cn(
        !isOnSbcFilterSidebar &&
          "rounded-sm text-center text-white outline-hidden border-[1px] focus:border-primary-dark-blue caret-primary-dark-blue",
        !isOnSbcFilterSidebar &&
          (value && !isFocused
            ? "bg-primary-dark-blue border-primary-dark-blue hover:bg-primary-washed-blue hover:border-primary-washed-blue font-medium"
            : "border-black/0 bg-black/30 hover:bg-black/75"),
        !isOnSbcFilterSidebar && (large ? "w-[55px] " : "w-[35px]"),
        isOnSbcFilterSidebar &&
          !newDesignSystem &&
          cn(
            "w-17.5 h-6 text-center outline-hidden rounded-sm",
            isFocused
              ? "bg-inner-box"
              : "bg-panel-background hover:bg-new-gray-700",
          ),
        isOnSbcFilterSidebar && !newDesignSystem && isMobile && "w-17.5 h-9",
        newDesignSystem &&
          cn(
            "h-8 w-16 rounded-lg border text-center text-sm text-neutral-100 outline-hidden transition-colors placeholder:text-neutral-500",
            isFocused
              ? "border-neutral-500 bg-neutral-900"
              : "border-neutral-700 bg-neutral-900 hover:border-neutral-600",
          ),
      )}
      placeholder={
        Number(placeHolder) === Number.MAX_VALUE ? t("max") : placeHolder
      }
      type="number"
      min={min || 1}
      max={max || 99}
      onChange={(e) => {
        const parsedValue = parseInt(e.target.value);
        if (isNaN(parsedValue)) {
          onChange(null);
        } else {
          const effectiveMax = max || 99;
          // A multi-digit min only clamps on blur, else typing "8" toward 85 jumps to 75
          const effectiveMin = min && min < 10 ? min : 1;
          onChange(Math.max(effectiveMin, Math.min(effectiveMax, parsedValue)));
        }
      }}
      onFocus={onFocus}
      onBlur={(e) => {
        max = max || 99;
        min = min || 1;
        if (parseInt(e.target.value) < min) onChange(min);
        else if (parseInt(e.target.value) > max) onChange(max);
        onBlur(parseInt(e.target.value));
      }}
      value={value || ""}
      // onKeyDown={(e) => e.key === "Escape" && e.currentTarget.blur()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          e.currentTarget.blur();
        }
      }}
    />
  );
};
